import React, { useEffect, useState } from 'react'
import { Sheet, SheetClose, SheetContent, SheetTrigger } from 'src/components/ui/sheet'
import { Bundle } from '../page'
import { ChevronLeft, ChevronRight, RotateCwIcon, SunIcon, Trash2Icon, XIcon } from 'lucide-react';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import lodash from 'lodash'
import { Drawer, DrawerContent, DrawerTrigger } from 'src/components/ui/drawer';
import { Label } from 'src/components/ui/label';
import { Slider } from 'src/components/ui/slider';
import { Switch } from 'src/components/ui/switch';

type EditBundleProps = {
    bundle: Bundle;
    onSaveBundle?: (bundle:Bundle) => void;

    trigger?: React.ReactNode;
}

const EditBundle = ({bundle, onSaveBundle, trigger}:EditBundleProps) => {
    const [open, setopen] = useState(false);
    const [selected_page, setselected_page] = useState(1);
    const [modified_bundle, setmodified_bundle] = useState(bundle);

    const [active_tab_modify, setactive_tab_modify] = useState('brightness');
    const [toggle_all_modify, settoggle_all_modify] = useState(false);

    const [rotate, setrotate] = useState<{index:number, value:number}[]>([]);
    const [brightness, setbrightness] = useState<{index:number, value:number}[]>([]); // index of documents inside bundle + value (min: -100 ; max: 100)
    const [contrast, setcontrast] = useState<{index:number, value:number}[]>([]); // index of documents inside bundle + value (min: -100 ; max: 100)

    const [previewImage, setPreviewImage] = useState<string>(''); // State to hold the preview image base64

    useEffect(()=>{ open && setmodified_bundle(bundle) }, [open])

    const handleCloseSheet = () => { setopen(false); }

    const handleSaveSheet = async () => {
        const newBundle = lodash.cloneDeep(modified_bundle);
        
        const updatedDocuments = await Promise.all(newBundle.documents.map(async (document, i) => {
            return await updatePreviewImage(document);
        }));
    
        newBundle.documents = updatedDocuments;
    
        onSaveBundle&&onSaveBundle(newBundle);
        handleCloseSheet()
    };

    const rotateBase64Image = (base64Image: string, degrees: number): Promise<string> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) return reject(new Error('Canvas context not available'));
    
                // Calculate rotated dimensions
                const radians = degrees * Math.PI / 180;
                const rotatedWidth = Math.abs(img.width * Math.cos(radians)) + Math.abs(img.height * Math.sin(radians));
                const rotatedHeight = Math.abs(img.width * Math.sin(radians)) + Math.abs(img.height * Math.cos(radians));
    
                canvas.width = rotatedWidth;
                canvas.height = rotatedHeight;
    
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.save();
                ctx.translate(canvas.width / 2, canvas.height / 2);
                ctx.rotate(radians);
                ctx.drawImage(img, -img.width / 2, -img.height / 2);
                ctx.restore();
    
                const rotatedBase64Image = canvas.toDataURL();
                resolve(rotatedBase64Image);
            };
            img.onerror = (error) => reject(error);
            img.src = base64Image;
        });
    };

    const adjustImage = (base64Image: string, brightnessVal: number, contrastVal: number): Promise<string> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) return reject(new Error('Canvas context not available'));
    
                canvas.width = img.width;
                canvas.height = img.height;
    
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const data = imageData.data;
    
                for (let i = 0; i < data.length; i += 4) {
                    data[i] += 255 * (brightnessVal / 100);     // Red
                    data[i + 1] += 255 * (brightnessVal / 100); // Green
                    data[i + 2] += 255 * (brightnessVal / 100); // Blue
    
                    data[i] = ((data[i] - 128) * (contrastVal / 100 + 1)) + 128;       // Red
                    data[i + 1] = ((data[i + 1] - 128) * (contrastVal / 100 + 1)) + 128; // Green
                    data[i + 2] = ((data[i + 2] - 128) * (contrastVal / 100 + 1)) + 128; // Blue
                }
    
                ctx.putImageData(imageData, 0, 0);
    
                const adjustedBase64Image = canvas.toDataURL();
                resolve(adjustedBase64Image);
            };
            img.src = base64Image;
        });
    };

    const handleRotateImage = () => {
        const newRotate = [...rotate];
        newRotate[selected_page - 1] = { index: selected_page - 1, value: (rotate[selected_page - 1]?.value || 0) + 90 };

        if(toggle_all_modify){
            for (let i = 0; i < modified_bundle.documents.length; i++) {
                newRotate[i] = { index:i, value: (rotate[selected_page - 1]?.value || 0) + 90 };
            }
        }

        setrotate(newRotate);
    };

    const handleChangeBrightnessImage = (value: number) => {
        const newBrightness = [...brightness];
        newBrightness[selected_page - 1] = { index: selected_page - 1, value };

        if(toggle_all_modify){
            for (let i = 0; i < modified_bundle.documents.length; i++) {
                newBrightness[i] = { index:i, value };
            }
        }

        setbrightness(newBrightness);
    };

    const handleChangeContrastImage = (value: number) => {
        const newContrast = [...contrast];
        newContrast[selected_page - 1] = { index: selected_page - 1, value };

        if(toggle_all_modify){
            for (let i = 0; i < modified_bundle.documents.length; i++) {
                newContrast[i] = { index:i, value };
            }
        }

        setcontrast(newContrast);
    };

    const handleDeleteImage = () => {
        const newBundle = lodash.cloneDeep(modified_bundle)
        newBundle.documents.splice(selected_page-1, 1)
        setmodified_bundle(newBundle);
        setselected_page(1)
    }

    const updatePreviewImage = async (base?:string) => {
        let newBase64 = base ? base : modified_bundle.documents[selected_page - 1];
        try {
            const brightValue = brightness.find(i=>i?.index === selected_page-1)?.value || 0
            const constrValue = contrast.find(i=>i?.index === selected_page-1)?.value || 0
            const rotateValue = rotate.find(i=>i?.index === selected_page-1)?.value || 0
            
            // Apply brightness and contrast adjustments
            if (brightValue || constrValue) {
                newBase64 = await adjustImage(newBase64, brightValue, constrValue);
            }
            
            // Apply rotation
            if (rotateValue) {
                newBase64 = await rotateBase64Image(newBase64, rotateValue);
            }

            // Update previewImage state with the processed base64
            setPreviewImage(newBase64);
            return newBase64
        } catch (error) {
            console.error('Failed to update preview image:', error);
            setPreviewImage(''); // Clear preview image state on error
            return newBase64 
        }
    };

    useEffect(() => {
        updatePreviewImage(); // Trigger updatePreviewImage when selected_page or adjustment values change
    }, [selected_page, brightness, contrast, rotate, modified_bundle]);

    useEffect(()=>{
        if(toggle_all_modify){
            const newContrast = [...contrast];
            const newRotate = [...rotate];
            const newBrightness = [...brightness]

            const currentContrast = newContrast.find(i=>i?.index===selected_page-1)?.value || 0
            const currentBrightness = newBrightness.find(i=>i?.index===selected_page-1)?.value || 0
            const currentRotate = newRotate.find(i=>i?.index===selected_page-1)?.value || 0

            for (let i = 0; i < modified_bundle.documents.length; i++) {
                newContrast[i] = { index:i, value:currentContrast };
                newBrightness[i] = { index:i, value:currentBrightness };
                newRotate[i] = { index:i, value:currentRotate };
            }

            setcontrast(newContrast);
            setrotate(newRotate);
            setbrightness(newBrightness);
        }
    }, [toggle_all_modify])

  return (
    <Sheet open={open} onOpenChange={(o)=>{setopen(o)}}>
        <SheetTrigger asChild>
            {trigger}
        </SheetTrigger>
        <SheetContent className='w-dvw max-w-[100dvw] flex flex-col justify-between h-dvh p-0 gap-0 bg-neutral-800 text-white border-none' hideClose position="left">
            <div className='flex flex-col justify-between h-full'>
                <div className='h-[50px] grid grid-cols-[50px,auto,50px] p-2 z-10 bg-neutral-800'>
                    <div className='flex justify-start items-center'>
                        <button onClick={handleCloseSheet} className='w-8 h-8 flex justify-center items-center'><XIcon className='w-6 h-6' strokeWidth={1.5}/></button>
                    </div>
                    <div className='flex justify-center items-center'>
                        <p>{bundle.name}</p>
                    </div>
                    <div className='flex justify-end items-center'></div>
                </div>

                <div className='h-[calc(100%-190px)] flex flex-col gap-4 p-4 items-center bg-neutral-600 relative'>
                    <div className='grid grid-cols-3 h-10 absolute left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] z-10'>
                        
                        <div className='flex items-center'>
                            {selected_page > 1 && <button onClick={()=>{setselected_page(p=>p-1)}} className='w-7 h-7 bg-black/60 border border-solid border-white/20 flex justify-center items-center rounded-full'><ChevronLeft className='w-4 h-4' strokeWidth={1.5}/></button>}
                        </div>

                        <div className='flex items-center justify-center'>
                            <p className='text-sm bg-black/60 px-3 py-1 rounded-full whitespace-nowrap'>Pagina {selected_page} van {modified_bundle.documents.length}</p>
                        </div>
                        
                        <div className='flex items-center justify-end'>
                            {selected_page !== modified_bundle.documents.length && <button onClick={()=>{setselected_page(p=>p+1)}} className='w-7 h-7 bg-black/60 border border-solid border-white/20 flex justify-center items-center rounded-full'><ChevronRight className='w-4 h-4' strokeWidth={1.5}/></button>}
                        </div>
                    </div>

                    <div className='flex justify-center items-center w-full max-h-full h-full'>
                        
                        <TransformWrapper minScale={0.5}>
                            <TransformComponent>
                                <img className='w-full h-full max-w-full' src={previewImage} />
                            </TransformComponent>
                        </TransformWrapper>
                    </div>
                    
                </div>
                
                <div className='h-[140px] flex flex-col justify-end z-10 bg-neutral-800'>
                    <div className='flex items-center px-4 gap-6 overflow-x-auto overflow-y-hidden'>
                        <button onClick={()=>{handleRotateImage()}} className='flex flex-col items-center justify-center gap-2 text-xs text-neutral-300 whitespace-nowrap p-2'>
                            <RotateCwIcon className='w-5 h-5 min-h-5' strokeWidth={1.5}/>
                            Roteren
                        </button>
                        
                        <Drawer>
                            <DrawerTrigger asChild>
                                <button className='flex flex-col items-center justify-center gap-2 text-xs text-neutral-300 whitespace-nowrap p-2'>
                                    <SunIcon className='w-5 h-5 min-h-5' strokeWidth={1.5}/>
                                    Aanpassen
                                </button>
                            </DrawerTrigger>
                            <DrawerContent showOverlay={false} className='px-4 pb-4 bg-neutral-800 text-white border-none'>
                                <div className='flex flex-col gap-8'>
                                    <div className='flex items-center justify-evenly border-b border-neutral-700'>
                                        <button onClick={()=>{setactive_tab_modify('brightness')}} className={`${active_tab_modify === 'brightness' ? 'text-sky-500 font-medium' : 'text-white/50'} text-sm p-2`}>Helderheid</button>
                                        <button onClick={()=>{setactive_tab_modify('contrast')}} className={`${active_tab_modify === 'contrast' ? 'text-sky-500 font-medium' : 'text-white/50'} text-sm p-2`}>Contrast</button>
                                    </div>
                                    {active_tab_modify === 'brightness' && 
                                    <div className='flex flex-col gap-3'>
                                        <Label htmlFor='brightness' className='font-normal'>Helderheid {brightness.find(i=>i?.index===selected_page-1)?.value || 0}</Label>
                                        <Slider onValueChange={(v)=>handleChangeBrightnessImage(v[0])} value={[brightness.find(i=>i?.index === selected_page-1)?.value || 0]} id='brightness' min={-100} max={100}/>
                                    </div>
                                    }
                                    {active_tab_modify === 'contrast' && 
                                    <div className='flex flex-col gap-3'>
                                        <Label htmlFor='contrast' className='font-normal'>Contrast {contrast.find(i=>i?.index===selected_page-1)?.value || 0}</Label>
                                        <Slider onValueChange={(v)=>handleChangeContrastImage(v[0])} value={[contrast.find(i=>i?.index === selected_page-1)?.value || 0]} id='contrast' min={-100} max={100}/>
                                    </div>
                                    }
                                    <div className='flex items-center gap-4'>
                                        <Switch checked={toggle_all_modify} onCheckedChange={(c)=>{settoggle_all_modify(c)}} id='apply-all-pages'/>
                                        <Label className='font-normal text-xs' htmlFor='apply-all-pages'>Toepassen op alle pagina's</Label>
                                    </div>
                                </div>
                            </DrawerContent>
                        </Drawer>
                        
                        <button onClick={()=>{handleDeleteImage()}} className='flex flex-col items-center justify-center gap-2 text-xs text-neutral-300 whitespace-nowrap p-2'>
                            <Trash2Icon className='w-5 h-5 min-h-5' strokeWidth={1.5}/>
                            Verwijderen
                        </button>
                    </div>
                    <div className='flex justify-end items-center gap-2 px-4 py-4'>
                        <button onClick={handleCloseSheet} className='h-10 bg-transparent text-primary-foreground/60 rounded-full px-4 text-sm font-medium'>Annuleren</button>
                        
                        
                        <button onClick={()=>{handleSaveSheet()}} className='h-10 bg-primary text-primary-foreground rounded-full px-4 text-sm font-medium'>Opslaan</button>
                    </div>
                </div>
            </div>
        </SheetContent>
    </Sheet>
  )
}

export default EditBundle